import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Typography,
} from "@material-ui/core"
import AttachMoneyIcon from "@material-ui/icons/AttachMoney"
import CancelIcon from "@material-ui/icons/Cancel"
import CheckCircleIcon from "@material-ui/icons/CheckCircle"
import CloseIcon from "@material-ui/icons/Close"
import LocalShippingIcon from "@material-ui/icons/LocalShipping"
import OpenInNewIcon from "@material-ui/icons/OpenInNew"
import RefreshIcon from "@material-ui/icons/Refresh"
import { useCallback, useEffect, useState } from "react"
import { PICKNGO_LOGO_URL } from "../../../IntegracaoPickngo/assets"
import {
    buscarEntregaPickngo,
    cancelarEntregaPickngo,
    cotarEntregaPickngo,
    marcarProntoPickngo,
    solicitarEntregaPickngo,
} from "../services"

// Situações da Pick'n Go em que a entrega já terminou: não cabe marcar pronto nem cancelar.
const SITUACOES_ENCERRADAS = ["FINALIZADO", "CONCLUIDO", "RETORNANDO", "CANCELADO"]

const formatarMoeda = valor =>
    typeof valor === "number"
        ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor)
        : null

const LinhaInfo = ({ rotulo, valor }) =>
    valor || valor === 0 ? (
        <Box display="flex" justifyContent="space-between" style={{ gap: 12, padding: "3px 0" }}>
            <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                {rotulo}
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>
                {valor}
            </Typography>
        </Box>
    ) : null

const Aviso = ({ tipo, children }) => {
    const erro = tipo === "erro"
    return (
        <div
            style={{
                backgroundColor: erro ? "#fdecea" : "#e8f5e9",
                border: `1px solid ${erro ? "#f5c6cb" : "#a5d6a7"}`,
                borderRadius: 6,
                padding: "10px 12px",
            }}
        >
            <Typography
                variant="body2"
                style={{
                    color: erro ? "#8a1c1c" : "#2e7d32",
                    fontWeight: erro ? 400 : 600,
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                }}
            >
                {children}
            </Typography>
        </div>
    )
}

/**
 * Entrega do pedido na Pick'n Go: cotar, solicitar entregador, marcar pronto, acompanhar, atualizar
 * e cancelar. A situação chega pelo webhook no backend; "Atualizar" consulta a Pick'n Go na hora.
 */
export default function DetalhesEntregaPickngo({ aberto, onClose, pedido, autorizacao }) {
    const [carregando, setCarregando] = useState(false)
    const [entrega, setEntrega] = useState(null)
    const [cotacao, setCotacao] = useState(null)
    const [acao, setAcao] = useState(null) // ação em andamento: cotar | solicitar | pronto | cancelar | atualizar
    const [erro, setErro] = useState(null)
    const [sucesso, setSucesso] = useState(null)

    const carregar = useCallback(
        async atualizar => {
            if (!pedido?.id) return
            const resposta = await buscarEntregaPickngo(pedido.id, autorizacao, atualizar)
            if (resposta.sucesso) {
                setEntrega(resposta.entrega || null)
                if (resposta.mensagem) setErro(resposta.mensagem)
            } else {
                setErro(resposta.mensagem)
            }
        },
        [pedido?.id, autorizacao]
    )

    useEffect(() => {
        if (!aberto) return
        setErro(null)
        setSucesso(null)
        setCotacao(null)
        setCarregando(true)
        carregar(true).finally(() => setCarregando(false))
    }, [aberto, carregar])

    const executar = async (nome, chamada, mensagemSucesso) => {
        setAcao(nome)
        setErro(null)
        setSucesso(null)
        try {
            const resposta = await chamada()
            if (!resposta.sucesso) {
                setErro(resposta.mensagem || "Não foi possível concluir a operação.")
                return resposta
            }
            if (resposta.entrega !== undefined) setEntrega(resposta.entrega)
            if (mensagemSucesso) setSucesso(resposta.mensagem || mensagemSucesso)
            return resposta
        } finally {
            setAcao(null)
        }
    }

    const handleCotar = async () => {
        const resposta = await executar("cotar", () => cotarEntregaPickngo(pedido.id, autorizacao))
        if (resposta?.sucesso) setCotacao(resposta)
    }

    const handleSolicitar = () =>
        executar(
            "solicitar",
            () => solicitarEntregaPickngo(pedido.id, autorizacao),
            "Entregador solicitado na Pick'n Go."
        )

    const handlePronto = () =>
        executar("pronto", () => marcarProntoPickngo(pedido.id, autorizacao), "Pedido marcado como pronto.")

    const handleCancelar = () => {
        if (!window.confirm("Deseja mesmo cancelar a entrega na Pick'n Go?")) return
        executar("cancelar", () => cancelarEntregaPickngo(pedido.id, autorizacao), "Entrega cancelada.")
    }

    const handleAtualizar = async () => {
        setAcao("atualizar")
        setErro(null)
        setSucesso(null)
        try {
            await carregar(true)
        } finally {
            setAcao(null)
        }
    }

    const ocupado = !!acao
    const status = entrega?.status
    const entregaAtiva = !!entrega?.ativo
    const entregaEncerrada = SITUACOES_ENCERRADAS.includes(status)
    // Pedido entregue (4) ou cancelado (< 0) na Cliente Fiel: não se solicita nem cancela entregador.
    const pedidoFinalizado = pedido?.status === 4 || pedido?.status < 0
    const link = entrega?.urlAcompanhamento
    const temLink = typeof link === "string" && /^https?:\/\//i.test(link.trim())

    const podeSolicitar = !pedidoFinalizado && !entregaAtiva
    const podeMarcarPronto = entregaAtiva && status === "RECEBIDO"
    const podeCancelar = entregaAtiva && !entregaEncerrada && !pedidoFinalizado

    const icone = (nome, IconeBase) =>
        acao === nome ? <CircularProgress size={14} color="inherit" /> : <IconeBase fontSize="small" />

    return (
        <Dialog open={!!aberto} onClose={onClose} maxWidth="xs" fullWidth onClick={e => e.stopPropagation()}>
            <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
                <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                    <img src={PICKNGO_LOGO_URL} alt="Pick'n Go" style={{ height: 24, borderRadius: 4 }} />
                    <Typography variant="h6" style={{ flexGrow: 1, fontSize: 18 }}>
                        Entrega — Pick'n Go
                    </Typography>
                    <IconButton size="small" aria-label="Fechar" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {carregando ? (
                    <Grid container justifyContent="center" style={{ padding: 24 }}>
                        <CircularProgress size={28} />
                    </Grid>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <LinhaInfo rotulo="Pedido" valor={pedido?.id ? `#${pedido.id}` : null} />
                            {entrega ? (
                                <>
                                    <LinhaInfo
                                        rotulo="Status da entrega"
                                        valor={entrega.statusDescricao || status}
                                    />
                                    <LinhaInfo rotulo="Entregador" valor={entrega.nomeEntregador} />
                                    <LinhaInfo rotulo="Telefone" valor={entrega.telefoneEntregador} />
                                    <LinhaInfo rotulo="Valor da entrega" valor={formatarMoeda(entrega.valorEntrega)} />
                                    <LinhaInfo
                                        rotulo="Código Pick'n Go"
                                        valor={entrega.pedidoPickngo ? entrega.pedidoPickngo.slice(-8) : null}
                                    />
                                </>
                            ) : (
                                <Typography variant="body2" style={{ color: "#7f8c8d", marginTop: 6 }}>
                                    Nenhum entregador solicitado para este pedido.
                                </Typography>
                            )}
                        </Grid>

                        {cotacao && (
                            <Grid item xs={12}>
                                <Box
                                    style={{
                                        backgroundColor: "#fff3e0",
                                        border: "1px solid #ffcc80",
                                        borderRadius: 6,
                                        padding: "10px 12px",
                                    }}
                                >
                                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                                        Cotação
                                    </Typography>
                                    <LinhaInfo rotulo="Valor" valor={formatarMoeda(cotacao.valor)} />
                                    <LinhaInfo
                                        rotulo="Distância"
                                        valor={typeof cotacao.distanciaKm === "number" ? `${cotacao.distanciaKm} km` : null}
                                    />
                                    <LinhaInfo rotulo="Veículo" valor={cotacao.veiculo} />
                                </Box>
                            </Grid>
                        )}

                        {erro && (
                            <Grid item xs={12}>
                                <Aviso tipo="erro">{erro}</Aviso>
                            </Grid>
                        )}
                        {sucesso && (
                            <Grid item xs={12}>
                                <Aviso tipo="sucesso">{sucesso}</Aviso>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                                {podeSolicitar && (
                                    <Box display="flex" style={{ gap: 8 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={icone("cotar", AttachMoneyIcon)}
                                            disabled={ocupado}
                                            onClick={handleCotar}
                                            style={{ textTransform: "none", whiteSpace: "nowrap" }}
                                        >
                                            Cotar
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            disableElevation
                                            startIcon={icone("solicitar", LocalShippingIcon)}
                                            disabled={ocupado}
                                            onClick={handleSolicitar}
                                            style={{ backgroundColor: "#e65100", color: "white", textTransform: "none" }}
                                        >
                                            {entrega ? "Solicitar novo entregador" : "Solicitar entregador"}
                                        </Button>
                                    </Box>
                                )}

                                {podeMarcarPronto && (
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={icone("pronto", CheckCircleIcon)}
                                        disabled={ocupado}
                                        onClick={handlePronto}
                                        style={{ backgroundColor: "#2e7d32", color: "white", textTransform: "none" }}
                                    >
                                        Marcar pronto para coleta
                                    </Button>
                                )}

                                {(entrega || temLink) && (
                                    <Box display="flex" style={{ gap: 8 }}>
                                        {podeCancelar && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={icone("cancelar", CancelIcon)}
                                                disabled={ocupado}
                                                onClick={handleCancelar}
                                                style={{
                                                    borderColor: "var(--cor-erro)",
                                                    color: "var(--cor-erro)",
                                                    whiteSpace: "nowrap",
                                                    textTransform: "none",
                                                }}
                                            >
                                                Cancelar
                                            </Button>
                                        )}
                                        {entrega && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={icone("atualizar", RefreshIcon)}
                                                disabled={ocupado}
                                                onClick={handleAtualizar}
                                                style={{ whiteSpace: "nowrap", textTransform: "none" }}
                                            >
                                                Atualizar
                                            </Button>
                                        )}
                                        {temLink && (
                                            <Button
                                                style={{ flexGrow: 1, textTransform: "none" }}
                                                size="small"
                                                color="primary"
                                                variant="contained"
                                                disableElevation
                                                startIcon={<OpenInNewIcon fontSize="small" />}
                                                href={link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Acompanhar
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    )
}
